
import * as React from 'react';
import { Link } from 'react-router-dom';
import {
  useNotifications,
  useNotificationStats,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  useArchiveNotification,
  usePerformNotificationAction,
  formatNotificationTime,
  type Notification,
  type NotificationType,
} from '../../lib/api/notifications';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Skeleton } from '../ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import {
  Bell,
  Package,
  UserCircle,
  FileWarning,
  AlertTriangle,
  DollarSign,
  AlertCircle,
  Wrench,
  MessageSquare,
  AtSign,
  Check,
  Archive,
  MoreHorizontal,
  Settings,
  CheckCheck,
  Clock,
  MapPin,
} from 'lucide-react';
import { cn } from '../../lib/utils';

// Icon mapping
const TypeIcons: Record<NotificationType, React.ElementType> = {
  load_status: Package,
  driver_status: UserCircle,
  document_required: FileWarning,
  compliance_alert: AlertTriangle,
  payment_received: DollarSign,
  invoice_overdue: AlertCircle,
  maintenance_due: Wrench,
  system_alert: Bell,
  message: MessageSquare,
  mention: AtSign,
  schedule_deviation: Clock,
  eta_update: MapPin,
};

// Color mapping
const TypeColors: Record<NotificationType, string> = {
  load_status: 'text-blue-500',
  driver_status: 'text-green-500',
  document_required: 'text-yellow-500',
  compliance_alert: 'text-orange-500',
  payment_received: 'text-emerald-500',
  invoice_overdue: 'text-red-500',
  maintenance_due: 'text-purple-500',
  system_alert: 'text-gray-500',
  message: 'text-blue-500',
  mention: 'text-indigo-500',
  schedule_deviation: 'text-amber-500',
  eta_update: 'text-cyan-500',
};

const PriorityColors = {
  urgent: 'border-l-4 border-l-red-500 bg-red-50',
  high: 'border-l-4 border-l-orange-500 bg-orange-50',
  normal: '',
  low: '',
};

interface NotificationItemProps {
  notification: Notification;
  onRead: (id: string) => void;
  onArchive: (id: string) => void;
  onAction: (id: string, action: string) => void;
}

function NotificationItem({ notification, onRead, onArchive, onAction }: NotificationItemProps) {
  const Icon = TypeIcons[notification.type];
  const iconColor = TypeColors[notification.type];
  const priorityStyle = PriorityColors[notification.priority];

  const handleClick = () => {
    if (notification.status === 'unread') {
      onRead(notification.id);
    }
  };

  const content = (
    <div
      className={cn(
        'p-3 hover:bg-muted/50 cursor-pointer transition-colors',
        notification.status === 'unread' && 'bg-blue-50/50',
        priorityStyle
      )}
      onClick={handleClick}
    >
      <div className="flex gap-3">
        <div className={cn('mt-0.5', iconColor)}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className={cn('text-sm font-medium truncate', notification.status === 'unread' && 'font-semibold')}>
              {notification.title}
            </p>
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {formatNotificationTime(notification.createdAt)}
            </span>
          </div>
          <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">
            {notification.message}
          </p>
          {notification.actionRequired && notification.actions && (
            <div className="flex gap-2 mt-2">
              {notification.actions.map((action, index) => (
                <Button
                  key={index}
                  variant={action.variant === 'default' ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 text-xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAction(notification.id, action.action);
                  }}
                >
                  {action.label}
                </Button>
              ))}
            </div>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {notification.status === 'unread' && (
              <Button
                variant="ghost"
                className="w-full justify-start text-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onRead(notification.id);
                }}
              >
                <Check className="mr-2 h-4 w-4" />
                Mark as read
              </Button>
            )}
            <Button
              variant="ghost"
              className="w-full justify-start text-sm"
              onClick={(e) => {
                e.stopPropagation();
                onArchive(notification.id);
              }}
            >
              <Archive className="mr-2 h-4 w-4" />
              Archive
            </Button>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {notification.status === 'unread' && (
        <div className="absolute left-1 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-blue-500" />
      )}
    </div>
  );

  if (notification.link) {
    return (
      <Link to={notification.link} className="block relative">
        {content}
      </Link>
    );
  }

  return <div className="relative">{content}</div>;
}

export function NotificationsDropdown() {
  const [open, setOpen] = React.useState(false);
  const { data: stats } = useNotificationStats({ enabled: open });
  const { data: notifications, isLoading } = useNotifications({ status: 'unread', limit: 10 }, { enabled: open });
  const { data: recentNotifications } = useNotifications({ limit: 10 }, { enabled: open });

  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const archive = useArchiveNotification();
  const performAction = usePerformNotificationAction();

  const handleMarkRead = (id: string) => {
    markRead.mutate(id);
  };

  const handleMarkAllRead = () => {
    markAllRead.mutate();
  };

  const handleArchive = (id: string) => {
    archive.mutate(id);
  };

  const handleAction = (id: string, action: string) => {
    performAction.mutate({ notificationId: id, action });
  };

  const displayNotifications = notifications?.data || recentNotifications?.data || [];
  const unreadCount = stats?.unreadCount || 0;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-[10px]"
              variant="destructive"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[400px] p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-semibold">Notifications</h3>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={handleMarkAllRead}
              >
                <CheckCheck className="mr-1 h-3 w-3" />
                Mark all read
              </Button>
            )}
            <Button variant="ghost" size="icon" className="h-7 w-7" asChild>
              <Link to="/settings?tab=notifications">
                <Settings className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>

        <ScrollArea className="h-[400px]">
          {isLoading ? (
            <div className="p-4 space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex gap-3">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : displayNotifications.length === 0 ? (
            <div className="p-8 text-center">
              <Bell className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No notifications</p>
            </div>
          ) : (
            <div className="divide-y">
              {displayNotifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onRead={handleMarkRead}
                  onArchive={handleArchive}
                  onAction={handleAction}
                />
              ))}
            </div>
          )}
        </ScrollArea>

        <DropdownMenuSeparator />
        <div className="p-2">
          <Button variant="ghost" className="w-full text-sm" asChild>
            <Link to="/notifications">View all notifications</Link>
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
